/** API v2 shapes used by the web app. */

export interface Filing {
  id: number
  symbol: string
  company_name: string | null
  exchange: 'NSE' | 'BSE'
  headline: string | null
  ai_summary_en: string | null
  ai_summary_hi: string | null
  category: string | null
  exchange_category: string | null
  impact_score: number | null
  direction: 'up' | 'down' | 'neutral' | 'mixed' | null
  filed_at: string
  pdf_url: string | null
  source_url: string | null
  sector: string | null
  mcap_bucket: 'large' | 'mid' | 'small' | 'micro' | null
  /** Only present when prices are enabled. */
  ltp?: number | null
  /** Close of the last session before the filing (base for since_filing_pct). */
  base_price?: number | null
  since_filing_pct?: number | null
}

export interface Page<T> { data: T[]; next_cursor: string | null; has_more: boolean; delay_minutes: number }

export type TabKey = 'all' | 'results' | 'actions' | 'board' | 'insider' | 'ratings' | 'orders'

export interface FilingStats {
  filings_today: number
  high_impact_today: number
  on_holdings_today: number
  holdings_count: number
  median_filing_to_alert_s: number | null
  delay_minutes: number
}

export interface WatchlistItem {
  symbol: string
  name: string | null
  filing_category: string | null
  filing_headline: string | null
  filing_at: string | null
  ltp: number | null
  change_pct: number | null
}
export interface Watchlist { id: number; name: string; items: WatchlistItem[] }

export interface CompanyHit { symbol: string; name: string; sector: string | null; mcap_bucket: string | null }

export const TAB_CATEGORIES: Record<TabKey, string[] | null> = {
  all: null,
  results: ['financial_results'],
  actions: ['dividend', 'bonus', 'stock_split', 'buyback'],
  board: ['board_meeting'],
  insider: ['insider_trading', 'pledge'],
  ratings: ['credit_rating'],
  orders: ['order_win'],
}

// ── P6: holdings, alerts, Risk Radar
export interface Holding {
  symbol: string; name: string; isin: string | null; quantity: number; avg_price: number | null; source: string
  updated_at: string; sector: string | null; filings_7d: number; risks_90d: number; next_action: string | null
  /** Live price from the market snapshot (null when prices are off / not quoted). */
  ltp?: number | null; change?: number | null; change_pct?: number | null
}
export interface ImportRow { symbol: string | null; isin: string | null; name: string | null; quantity: number | null; avg_price: number | null; status: 'matched' | 'unknown' | 'invalid_quantity' }
export interface ImportPreview { source: string; matched: ImportRow[]; unmatched: ImportRow[]; skipped_non_equity: number }

export type RiskKind = 'auditor_resignation' | 'rating_downgrade' | 'pledge_increase' | 'key_exit' | 'delayed_results' | 'regulator_penalty'
export interface RiskFlag {
  id: number; symbol: string; name: string; kind: RiskKind; label: string; detail: string; severity: 1 | 2 | 3
  flagged_at: string; filing_id: number | null; headline: string | null; pdf_url: string | null; held: boolean
}

export interface AlertRule {
  id: number; name: string; scope: 'symbol' | 'watchlist' | 'watchlists' | 'holdings' | 'all' | 'filters'
  symbol: string | null; watchlist_id: number | null; categories: string[]; exchange: string | null; sectors: string[]
  mcap_buckets: string[]; query: string | null; min_impact: number; directions: string[]; channels: string[]
  mode: 'instant' | 'digest'; is_active: boolean; migrated: boolean; created_at: string
}
export interface AlertSettings {
  preferences: { quiet_start: string | null; quiet_end: string | null; digest_preopen: boolean; digest_weekly: boolean; email_alerts: boolean }
  telegram: { linked: boolean; username: string | null; linked_at: string | null; enabled: boolean; bot: string | null; pending_link: string | null }
  email: { address: string; configured: boolean }
}
export interface DeliveryLog {
  id: number; kind: string; channel: string; status: 'queued' | 'sent' | 'failed' | 'skipped'; error: string | null
  created_at: string; delivered_at: string | null; latency_ms: number | null; rule_name: string | null
  filing_id: number | null; symbol: string | null; headline: string | null; category: string | null; impact_score: number | null
}
